import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  TrendingUp, 
  Target, 
  Award,
  UserPlus,
  DollarSign,
  Activity,
  BarChart3,
  LogOut,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import TeamManagement from './TeamManagement';
import LeadManagement from './LeadManagement';
import PerformanceDashboard from './PerformanceDashboard';
import PromotionsManagement from './PromotionsManagement';
import { API_URL } from '../../lib/api-client';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme, themes } from '../ThemeProvider';

interface DashboardStats {
  totalTeamMembers: number;
  activeTeamMembers: number;
  totalLeads: number;
  assignedLeads: number;
  unassignedLeads: number;
  convertedLeads: number;
  conversionRate: number;
  totalCommissions: number;
  thisMonthCommissions: number;
  teamPerformance: {
    userId: string;
    name: string;
    leadsAssigned: number;
    leadsConverted: number;
    totalCommissions: number;
  }[];
}

type ViewType = 'overview' | 'team' | 'leads' | 'performance' | 'promotions';

export default function MarketingManagerDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<ViewType>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPromotionsTab, setShowPromotionsTab] = useState(true);
  const { logout } = useAuthContext();
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  useEffect(() => {
    fetchStats();
    checkPromotionsPermission();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/marketing-manager/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data.stats);
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const checkPromotionsPermission = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/promotional-orders/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setShowPromotionsTab(data.data.showPromotionsTab !== false);
      }
    } catch (error) {
      // Silently fail, default to true
      console.error('Error checking promotions permission:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const menuItems = [
    { id: 'overview' as ViewType, icon: BarChart3, label: 'Overview' },
    { id: 'team' as ViewType, icon: Users, label: 'Team Management' },
    { id: 'leads' as ViewType, icon: Target, label: 'Lead Management' },
    { id: 'performance' as ViewType, icon: Activity, label: 'Performance' },
    ...(showPromotionsTab ? [{ id: 'promotions' as ViewType, icon: Sparkles, label: 'Promotions' }] : []),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-pulse text-lg">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
      >
        <Users className="size-6" />
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-gradient-to-b ${currentTheme.primary} text-white flex flex-col shadow-xl transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="p-6 border-b border-white/10">
          <h1 className="text-2xl font-bold">Marketing Manager</h1>
          <p className="text-sm text-white/70 mt-1">Team & Lead Management</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentView(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-white/20 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 overflow-auto md:ml-0">
        <div className="p-3 md:p-6 space-y-4 md:space-y-6">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 mt-12 md:mt-0">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
              {menuItems.find(item => item.id === currentView)?.label || 'Dashboard'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {currentView === 'overview' && 'View your team and performance metrics'}
              {currentView === 'team' && 'Manage your marketing team members'}
              {currentView === 'leads' && 'Assign and track leads'}
              {currentView === 'performance' && 'Analyze team performance and metrics'}
              {currentView === 'promotions' && 'Manage and track promotions'}
            </p>
          </div>

          {/* Stats Overview - Show on all views */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Team Members</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalTeamMembers}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.activeTeamMembers} active
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
                  <Target className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalLeads}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.unassignedLeads} unassigned
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.conversionRate.toFixed(1)}%</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.convertedLeads} converted
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">Commissions</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">₹{stats.thisMonthCommissions.toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">
                    This month
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Content based on current view */}
          {currentView === 'overview' && (
            <Card>
              <CardHeader>
                <CardTitle>Team Performance Overview</CardTitle>
                <CardDescription>
                  Top performing team members this month
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats && stats.teamPerformance.length > 0 ? (
                  <div className="space-y-4">
                    {stats.teamPerformance.map((member, index) => (
                      <div key={member.userId} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {member.leadsAssigned} leads • {member.leadsConverted} converted
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">₹{(member.totalCommissions || 0).toFixed(2)}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">commissions</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No team performance data available
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {currentView === 'team' && <TeamManagement onUpdate={fetchStats} />}
          {currentView === 'leads' && <LeadManagement onUpdate={fetchStats} />}
          {currentView === 'performance' && <PerformanceDashboard />}
          {currentView === 'promotions' && showPromotionsTab && <PromotionsManagement />}
        </div>
      </div>
    </div>
  );
}